<template>
  <div>
    <h2>Training Reservations</h2>

    <button v-if="canCreate" @click="openCreate">+ Add Reservation</button>
    <div v-else class="permission-info">
      Only Team Leaders can create reservations
    </div>

    <table v-if="reservations.length > 0">
      <thead>
        <tr>
          <th>Start</th>
          <th>End</th>
          <th>Project</th>
          <th>Time</th>
          <th>Persons</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="reservation in reservations" :key="reservation.id">
          <td>{{ formatDate(reservation.start_date) }}</td>
          <td>{{ formatDate(reservation.end_date) }}</td>
          <td>{{ reservation.project_type }}</td>
          <td>{{ reservation.start_time }} - {{ reservation.end_time }}</td>
          <td>{{ reservation.number_of_persons }}</td>
          <td>
            <span :class="['status', reservation.status]">
              {{ reservation.status }}
            </span>
          </td>
          <td>
            <i
              v-if="canEdit(reservation)"
              class="fas fa-pen btn-edit"
              @click="editReservation(reservation)"
            />

            <i
              v-if="canConfirm(reservation)"
              class="fas fa-check btn-status"
              @click="confirmReservation(reservation)"
            />

            <i
              v-if="canCancel(reservation)"
              class="fas fa-times btn-status"
              @click="cancelReservation(reservation)"
            />

            <i
              v-if="canDelete(reservation)"
              class="fas fa-trash btn-delete"
              @click="deleteReservation(reservation.id)"
            />
          </td>
        </tr>
      </tbody>
    </table>

    <div v-else class="empty-state">
      No reservations found.
    </div>

    <TrainingReservationForm
      v-if="showForm"
      :reservation="selected"
      @close="closeForm"
      @saved="fetchReservations"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import TrainingReservationForm from './TrainingReservationForm.vue'
import {
  getReservations,
  deleteReservation as apiDeleteReservation,
  updateStatus
} from '@/services/trainingReservations'
import { getCurrentUser } from '@/utils/permissions'

const reservations = ref([])
const showForm = ref(false)
const selected = ref(null)
const currentUser = ref(getCurrentUser?.() || {})

const roleName = computed(() => {
  return currentUser.value?.role || currentUser.value?.roles?.[0]?.name || null
})

const positionCode = computed(() => {
  return (
    currentUser.value?.professionalInformation?.position?.code ||
    currentUser.value?.position_code ||
    null
  )
})

const userId = computed(() => currentUser.value?.id || null)

const isTL = computed(() => positionCode.value === 'TL')
const isMaterialManager = computed(() => roleName.value === 'Material Manager')
const isSuperAdmin = computed(() => roleName.value === 'SuperAdmin')

const canCreate = computed(() => isTL.value)

const canEdit = (reservation) => {
  return (
    isTL.value &&
    reservation.user_id === userId.value &&
    reservation.status === 'pending'
  )
}

const canConfirm = (reservation) => {
  return isMaterialManager.value && reservation.status === 'pending'
}

const canCancel = (reservation) => {
  return isMaterialManager.value && reservation.status === 'pending'
}

const canDelete = (reservation) => {
  const isOwner = reservation.user_id === userId.value

  if (isSuperAdmin.value) {
    return ['pending', 'cancelled'].includes(reservation.status)
  }

  if (isMaterialManager.value) {
    return ['pending', 'cancelled'].includes(reservation.status)
  }

  if (isOwner) {
    return reservation.status === 'pending'
  }

  return false
}

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const fetchReservations = async () => {
  try {
    const res = await getReservations()
    reservations.value = res.data.data || res.data || []
  } catch (error) {
    alert(error.response?.data?.message || error.message)
  }
}

const openCreate = () => {
  selected.value = null
  showForm.value = true
}

const editReservation = (reservation) => {
  selected.value = reservation
  showForm.value = true
}

const closeForm = () => {
  showForm.value = false
  fetchReservations()
}

const confirmReservation = async (reservation) => {
  try {
    await updateStatus(reservation.id, 'confirmed')
    fetchReservations()
  } catch (error) {
    alert(error.response?.data?.message || error.message)
  }
}

const cancelReservation = async (reservation) => {
  const refusalReason = prompt('Reason for cancellation:') || ''

  try {
    const data = {}
    if (refusalReason) data.refusal_reason = refusalReason

    await updateStatus(reservation.id, 'cancelled', data)
    fetchReservations()
  } catch (error) {
    alert(error.response?.data?.message || error.message)
  }
}

const deleteReservation = async (id) => {
  if (!confirm('Are you sure?')) return

  try {
    await apiDeleteReservation(id)
    fetchReservations()
  } catch (error) {
    alert(error.response?.data?.message || error.message)
  }
}

onMounted(fetchReservations)
</script>

<style scoped>
.permission-info {
  background: #f3f4f6;
  color: #6b7280;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  text-align: center;
  font-style: italic;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #6b7280;
}

.status {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status.pending {
  background: #fef3c7;
  color: #92400e;
}

.status.confirmed {
  background: #dcfce7;
  color: #166534;
}

.status.cancelled {
  background: #f3f4f6;
  color: #6b7280;
}

.btn-edit,
.btn-status,
.btn-delete {
  cursor: pointer;
  padding: 4px 8px;
  margin: 0 4px;
  border-radius: 4px;
}

.btn-edit:hover {
  background: #e0f2fe;
}

.btn-status:hover {
  background: #fef3c7;
}

.btn-delete:hover {
  background: #fee2e2;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
}

th,
td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
}

td {
  color: #6b7280;
}
</style>